import type { LdCheckoutContext } from '@/utils/ldCheckoutContext';
import {
  clearLdCheckoutContext,
  getLdCheckoutContext,
  setLdCheckoutContext,
} from '@/utils/ldCheckoutContext';
import { Tracker } from '@/utils/tracking';
import {
  BNPL_PROVIDER_UNKNOWN,
  DECLINE_REASON_UNKNOWN,
  LD_EVENTS,
  type LdBookingAttemptedPayload,
  type LdPaymentResultValue,
  buildLdAttemptedPayload,
  buildLdResultPayload,
  resolveLdMetricValue,
} from '@/utils/tracking/ldEvents';

/**
 * Opens the funnel for one Book press: persists the experiment context and
 * fires `ld-booking-attempted` from that same object, so the snapshot every
 * later fire site reads can never disagree with the event that opened it.
 *
 * Enrollment is recomputed on every read of `useBnplExperimentVariant` (it
 * depends on the live cart total and on LD having identified the client), so
 * pinning it once here is what stops a mid-flow cart change or guest→login from
 * silently dropping the result event.
 */
export const startLdCheckout = (
  context: Omit<LdCheckoutContext, 'createdAt'>,
): void => {
  if (!context.enrolled) return;

  setLdCheckoutContext(context);

  const payload: LdBookingAttemptedPayload = {
    method: context.method,
    // Stripe resolves the concrete provider later, inside the BNPL sheet.
    bnpl_provider: BNPL_PROVIDER_UNKNOWN,
    variant: context.variant,
    shop_id: context.shopId,
    entry_type: context.entryType,
  };

  Tracker.track(LD_EVENTS.bookingAttempted, payload, {
    whitelist: ['launchDarkly'],
  });
};

/**
 * Every non-BNPL fire site funnels through here rather than inlining the
 * snapshot read: the enrollment gate, the mandatory `whitelist: ['launchDarkly']`
 * and the clear-only-on-result rule then live in exactly one place. LaunchDarkly
 * is deliberately absent from every destination preset, so an unwhitelisted
 * `Tracker.track` would never reach it.
 */
export const trackLdPaymentAttempted = (cartId: string): void => {
  const context = getLdCheckoutContext(cartId);
  if (!context || !context.enrolled) return;

  Tracker.track(LD_EVENTS.paymentAttempted, buildLdAttemptedPayload(context), {
    whitelist: ['launchDarkly'],
  });

  // Deliberately does not clear the snapshot: the result event fires later and
  // reads the same one. Clearing here would silently drop every result.
};

export const trackLdPaymentResult = (
  cartId: string,
  result: LdPaymentResultValue,
  declineReason: string = DECLINE_REASON_UNKNOWN,
): void => {
  const context = getLdCheckoutContext(cartId);
  if (!context || !context.enrolled) return;

  Tracker.track(
    LD_EVENTS.paymentResult,
    buildLdResultPayload(context, result, declineReason),
    {
      whitelist: ['launchDarkly'],
      metricValue: resolveLdMetricValue(context, result),
    },
  );

  // Caps each Book press at one result event: a retry on the same cart finds no
  // snapshot and stays silent rather than double-counting the experiment unit.
  clearLdCheckoutContext();
};
